import { FC, useEffect, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../redux-store/CustomReduxHooks";
import {
  resetSecondaryChannelSlice,
  updateSecondaryChannelState,
} from "./SecondaryChannel_Slice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDecryptedSearchParamsValue } from "../../axios/axiosEncryptionUtils";
import {
  resetCsAppSliceToInitialState,
  updateChannelDetails,
} from "../../redux-store/reducers/CsAppSlice";
import {
  E_CampaignIdentifierKeys,
  E_ChannelOptions,
  J_FunctionalityCode,
  T_CampaignHeaders,
} from "../../globalTypes/GlobalTypes";
import CsSessionStorageUtils from "../../utils/CsSessionStorageUtils";
import { updateLoadingState } from "../Feature_Loading/Loading_Slice";
import NotFound from "../../pages/NotFound/NotFound";
import CsLocalStorageUtils from "../../utils/CsLocalStorageUtils";
import { isEmpty } from "lodash";
import {
  BIOMETRIC_DO_IT_LATER_STATUS,
  eraseCookie,
} from "../../utils/CSCookieHandler";
import { REGISTER_BIO_METRIC } from "../../utils/constants";
import { resetLoginData } from "../Feature_Login/Login_Slice";
import { clearSsoSlice } from "../Feature_SSO/Sso_Slice";

const Feature_SecondaryChannel: FC = () => {
  const [urlSearchParam] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<any>("");
  const { tokenDetails } = useAppSelector((state) => state.CsAppSlice);

  const handleSecondaryChannelWithConfig = (configurationData: any) => {
    const request = urlSearchParam.get("request");
    if (!request) {
      dispatch(updateLoadingState(false));
      setIsError(true);
      return;
    }
    const requestData: any = getDecryptedSearchParamsValue(
      request,
      configurationData,
    );
    const mobileNoFromStorage = CsLocalStorageUtils.get("MOBILE");
    if (
      !isEmpty(mobileNoFromStorage) &&
      !isEmpty(requestData.mobileNo) &&
      mobileNoFromStorage !== requestData.mobileNo
    ) {
      console.log("in mobile mismatch case, clear data and reload");
      dispatch(resetCsAppSliceToInitialState());
      dispatch(resetLoginData());
      dispatch(resetSecondaryChannelSlice());
      dispatch(clearSsoSlice());
      window.localStorage.clear();
      window.sessionStorage.clear();
      eraseCookie(BIOMETRIC_DO_IT_LATER_STATUS);
      eraseCookie(REGISTER_BIO_METRIC);
      window.location.reload();
      return;
    }

    const campaignRelatedParams: T_CampaignHeaders[] = [];
    const campaignIdentifierKeys = [
      E_CampaignIdentifierKeys.utm_source,
      E_CampaignIdentifierKeys.utm_medium,
      E_CampaignIdentifierKeys.utm_campaign,
      E_CampaignIdentifierKeys.utm_term,
      E_CampaignIdentifierKeys.utm_content,
      E_CampaignIdentifierKeys.utm_id,
      E_CampaignIdentifierKeys.utm_adid,
      E_CampaignIdentifierKeys.utm_adgroup,
    ];
    campaignIdentifierKeys.forEach((identifierKey: string) => {
      campaignRelatedParams.push({
        key: identifierKey,
        value: requestData[identifierKey] || "",
      });
    });
    CsSessionStorageUtils.set(
      "CAMPAIGN_IDENTIFIERS",
      JSON.stringify(campaignRelatedParams),
    );
    const routeDetails = J_FunctionalityCode.find(
      (f) => f.funcCode === requestData.functionalityCode,
    );

    if (!routeDetails?.route) {
      throw new Error("Unable to map functionality to route");
    }

    const urlConstructed = routeDetails.route;
    const dynamicState: any = {};
    let dynamicKeys = routeDetails?.dynamicKeys || [];
    if (dynamicKeys?.length > 0) {
      routeDetails?.dynamicKeys.forEach((d) => {
        dynamicState[d] = requestData[d] || "";
      });
    }
    dispatch(updateChannelDetails(E_ChannelOptions.SECONDARY_CHANNEL));
    const stateInfo = {
      functionalityCode: requestData.functionalityCode,
      policyNo: requestData.policyNumber || "",
      source: requestData.source,
      constructedAppUrl: urlConstructed,
      panNumber: requestData.panNumber || "",
      dynamicState: dynamicState,
      mobileNo: requestData.mobileNo,
    };
    dispatch(updateSecondaryChannelState(stateInfo));
    if (!isEmpty(requestData.mobileNo)) {
      dispatch(updateLoadingState(true));
    }
    navigate(urlConstructed, { state: { ...dynamicState }, replace: true });
  };

  useEffect(() => {
    try {
      if (tokenDetails?.configuration?.iv) {
        try {
          handleSecondaryChannelWithConfig(tokenDetails?.configuration);
        } catch (error) {
          console.error(
            "Error validating secondary channel response with old configuration",
            error,
          );
        }
      } else {
        dispatch(updateLoadingState(true));
      }
    } catch (e: any) {
      console.error(e);
      dispatch(updateLoadingState(false));
      setErrorMessage(e);
      setIsError(true);
      return;
    }
  }, [tokenDetails?.configuration]);

  return isError ? (
    <NotFound
      hideButton={true}
      moduleName="Feature_SecondaryChannel"
      pageName="Feature_SecondaryChannel"
      errorTitle=""
      errorMessage={errorMessage}
    />
  ) : (
    <></>
  );
};
export default Feature_SecondaryChannel;
